/**
 * /admin/meters — admin CRUD over the meters table (§9.4).
 *
 * The table mirrors the API contract:
 *   - filters: site_slug, device_slug, kind, status — all optional, all
 *     forwarded as query params unchanged
 *   - per-row actions: Edit (PATCH) and Delete (DELETE → 409 → Retire)
 *   - Create button at the page top opens the same form modal in create mode
 *
 * Pagination is intentionally cursorless: limit/offset matches the API and
 * the dataset size on TEST (~25 ESS meters) is well under one page.
 */
import {
  ActionIcon,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconEdit,
  IconGauge,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAdminMeters } from "@/api/queries";
import {
  METER_KIND_SUGGESTIONS,
  METER_STATUSES,
  type AdminMeter,
} from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { MeterDeleteConfirm } from "@/components/MeterDeleteConfirm";
import { MeterFormModal } from "@/components/MeterFormModal";
import { SiteSelect } from "@/components/SiteSelect";

const PAGE_SIZE = 50;

type FiltersState = {
  site_slug: string;
  device_slug: string;
  kind: string;
  status: string;
};

const EMPTY_FILTERS: FiltersState = {
  site_slug: "",
  device_slug: "",
  kind: "",
  status: "",
};

type ModalState =
  | { kind: "none" }
  | { kind: "create" }
  | { kind: "edit"; meter: AdminMeter }
  | { kind: "delete"; meter: AdminMeter };

export function AdminMetersPage() {
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<ModalState>({ kind: "none" });

  // Trim+normalize filters into the shape the hook expects (undefined for
  // empty strings so the URL stays clean).
  const queryParams = useMemo(
    () => ({
      site_slug: filters.site_slug.trim() || undefined,
      device_slug: filters.device_slug.trim() || undefined,
      kind: filters.kind || undefined,
      status: filters.status || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, page],
  );

  const metersQuery = useAdminMeters(queryParams);

  const items = metersQuery.data?.items ?? [];
  const total = metersQuery.data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (k: keyof FiltersState, v: string) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Admin</Text>
        <Text>Meters</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={1}>Meters</Title>
          <Text c="dimmed" size="sm">
            Tenant-scoped meters across every site. Edit display name / kind /
            unit / status here; re-home a meter through its device endpoint
            (see DEVELOPER_CONTINUATION_GUIDE §9.3).
          </Text>
        </Stack>
        <Group>
          <Tooltip label="Refresh list">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => metersQuery.refetch()}
              loading={metersQuery.isFetching}
              aria-label="Refresh"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setModal({ kind: "create" })}
          >
            New meter
          </Button>
        </Group>
      </Group>

      <Paper withBorder p="md" radius="md">
        <Group gap="sm" wrap="wrap">
          <SiteSelect
            label="Site"
            placeholder="any"
            clearable
            value={filters.site_slug || null}
            onChange={(v) => setFilter("site_slug", v ?? "")}
            w={240}
          />
          <TextInput
            label="Device slug"
            placeholder="e.g. b_stack_0_0"
            leftSection={<IconSearch size={14} />}
            value={filters.device_slug}
            onChange={(e) => setFilter("device_slug", e.currentTarget.value)}
            w={200}
          />
          <Select
            label="Kind"
            placeholder="any"
            data={[...METER_KIND_SUGGESTIONS]}
            clearable
            value={filters.kind || null}
            onChange={(v) => setFilter("kind", v ?? "")}
            w={160}
          />
          <Select
            label="Status"
            placeholder="any"
            data={METER_STATUSES.map((s) => ({ value: s, label: s }))}
            clearable
            value={filters.status || null}
            onChange={(v) => setFilter("status", v ?? "")}
            w={160}
          />
        </Group>
      </Paper>

      {metersQuery.error && <ErrorBanner error={metersQuery.error} />}

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconGauge size={20} />
            <Title order={3}>Results</Title>
          </Group>
          <Text c="dimmed" size="sm">
            {total} meter{total === 1 ? "" : "s"}
          </Text>
        </Group>

        {metersQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : items.length === 0 ? (
          <Text c="dimmed">No meters match the current filters.</Text>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Site / Device</Table.Th>
                <Table.Th>Kind</Table.Th>
                <Table.Th>Unit</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((meter) => (
                <Table.Tr key={meter.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text fw={500}>{meter.display_name}</Text>
                      <Text c="dimmed" size="xs">
                        {meter.slug}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">
                        <Link to={`/sites/${meter.site_slug}`}>{meter.site_slug}</Link>
                      </Text>
                      <Text c="dimmed" size="xs">
                        {meter.device_slug ? (
                          <Link
                            to={`/sites/${meter.site_slug}/devices/${meter.device_slug}`}
                          >
                            {meter.device_slug}
                          </Link>
                        ) : (
                          "(ungrouped)"
                        )}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="default" tt="lowercase">
                      {meter.kind}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {meter.unit}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={
                        meter.status === "active"
                          ? "green"
                          : meter.status === "commissioning"
                            ? "blue"
                            : "gray"
                      }
                    >
                      {meter.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Edit ${meter.display_name}`}
                          onClick={() => setModal({ kind: "edit", meter })}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete (or retire)">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Delete ${meter.display_name}`}
                          onClick={() => setModal({ kind: "delete", meter })}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>

      <MeterFormModal
        opened={modal.kind === "create"}
        onClose={() => setModal({ kind: "none" })}
        mode="create"
      />
      <MeterFormModal
        opened={modal.kind === "edit"}
        onClose={() => setModal({ kind: "none" })}
        mode="edit"
        meter={modal.kind === "edit" ? modal.meter : undefined}
      />
      <MeterDeleteConfirm
        opened={modal.kind === "delete"}
        onClose={() => setModal({ kind: "none" })}
        meter={modal.kind === "delete" ? modal.meter : null}
      />
    </Stack>
  );
}
